import { useState, useEffect } from "react";
import { useLocation, useNavigate } from "react-router-dom";
import { motion, AnimatePresence } from "framer-motion";
import { ChevronDown, Menu, X, CalendarCheck, Globe } from "lucide-react";
import { Link, useLang } from "@/components/LangLink";
import { useContent } from "@/lib/content";
import { SUPPORTED_LANGS } from "@/i18n";

const Logo = ({ light = false }) => {
  const { CONTACT } = useContent();
  return (
    <Link to="/" data-testid="nav-logo" className="flex flex-col leading-none group">
      <img
        src={light ? "/brands/medisistem_white.png" : "/brands/medisistem_color.png"}
        alt="Medisistem"
        className="h-9 sm:h-10 w-auto transition-opacity duration-300"
      />
      <span className={`mt-1.5 pl-1 font-serif italic text-[11px] sm:text-xs transition-colors duration-300 ${light ? "text-white/90" : "text-green"}`}>
        {CONTACT.payoff}
      </span>
    </Link>
  );
};

const LanguageSwitcher = ({ onDark, mobile = false }) => {
  const lang = useLang();
  const navigate = useNavigate();
  const location = useLocation();
  const { COPY } = useContent();
  const pagePath = location.pathname.slice(1 + lang.length) || "/";

  if (SUPPORTED_LANGS.length < 2) return null;
  const go = (l) => {
    if (l === lang) return;
    navigate(`/${l}${pagePath === "/" ? "" : pagePath}${location.hash || ""}`);
  };

  return (
    <div className={`flex items-center gap-1 ${mobile ? "mt-2" : ""}`} data-testid="language-switcher" aria-label={COPY.nav.language}>
      <Globe size={15} className={mobile ? "text-navy/50" : onDark ? "text-white/70" : "text-navy/50"} />
      {SUPPORTED_LANGS.map((l, i) => (
        <span key={l} className="flex items-center">
          {i > 0 && <span className={`mx-0.5 text-xs ${mobile ? "text-navy/30" : onDark ? "text-white/40" : "text-navy/30"}`}>/</span>}
          <button
            type="button"
            data-testid={`lang-${l}`}
            onClick={() => go(l)}
            className={`text-xs font-semibold uppercase transition-colors ${
              l === lang
                ? "text-green"
                : mobile
                ? "text-navy/60 hover:text-green"
                : onDark
                ? "text-white/75 hover:text-white"
                : "text-navy/70 hover:text-green"
            }`}
          >
            {l}
          </button>
        </span>
      ))}
    </div>
  );
};

const Navbar = () => {
  const [scrolled, setScrolled] = useState(false);
  const [open, setOpen] = useState(false);
  const [prodOpen, setProdOpen] = useState(false);
  const [mobileProd, setMobileProd] = useState(false);
  const { pathname, hash } = useLocation();
  const lang = useLang();
  const { NAV, COPY } = useContent();
  const onDark = !scrolled;
  const pagePath = pathname.slice(1 + lang.length) || "/";

  useEffect(() => {
    const onScroll = () => setScrolled(window.scrollY > 24);
    onScroll();
    window.addEventListener("scroll", onScroll);
    return () => window.removeEventListener("scroll", onScroll);
  }, []);

  useEffect(() => {
    setOpen(false);
    setMobileProd(false);
  }, [pathname, hash]);

  return (
    <header
      className={`fixed top-0 inset-x-0 z-50 transition-[background-color,box-shadow,backdrop-filter] duration-500 ${
        scrolled ? "bg-white/85 backdrop-blur-xl border-b border-[#E7EDF2] shadow-[0_4px_30px_rgba(15,25,35,0.05)]" : "bg-transparent"
      }`}
      data-testid="main-navbar"
    >
      <nav className="container-x flex items-center justify-between h-[76px] lg:h-[92px]">
        <Logo light={onDark} />

        <div className="hidden lg:flex items-center gap-1">
          {NAV.map((item) =>
            item.children ? (
              <div
                key={item.label}
                className="relative"
                onMouseEnter={() => setProdOpen(true)}
                onMouseLeave={() => setProdOpen(false)}
              >
                <Link
                  to={item.to}
                  data-testid="nav-prodotti"
                  className={`flex items-center gap-1 px-3 py-2 text-sm font-medium transition-colors duration-200 ${
                    onDark ? "text-white/85 hover:text-white" : "text-navy hover:text-green"
                  }`}
                >
                  {item.label}
                  <ChevronDown size={15} className={`transition-transform duration-300 ${prodOpen ? "rotate-180" : ""}`} />
                </Link>
                <AnimatePresence>
                  {prodOpen && (
                    <motion.div
                      initial={{ opacity: 0, y: 10 }}
                      animate={{ opacity: 1, y: 0 }}
                      exit={{ opacity: 0, y: 10 }}
                      transition={{ duration: 0.2 }}
                      className="absolute left-0 top-full pt-3 w-[21rem]"
                    >
                      <div className="rounded-xl border border-[#E7EDF2] bg-white p-2 shadow-[0_20px_50px_-15px_rgba(15,25,35,0.2)]">
                        <Link
                          to={item.to}
                          data-testid="nav-dd-tutti-i-prodotti"
                          className="block rounded-lg px-4 py-2.5 text-sm font-semibold leading-snug text-navy hover:bg-cloud hover:text-green transition-colors duration-200"
                        >
                          Tutti i prodotti
                        </Link>
                        <div className="my-1 h-px bg-[#E7EDF2]" />
                        {item.children.map((c) => (
                          <Link
                            key={c.to}
                            to={c.to}
                            data-testid={`nav-dd-${c.label.toLowerCase().replace(/\s+/g, "-")}`}
                            className="block rounded-lg px-4 py-2.5 text-sm font-medium leading-snug text-navy hover:bg-cloud hover:text-green transition-colors duration-200"
                          >
                            {c.label}
                          </Link>
                        ))}
                      </div>
                    </motion.div>
                  )}
                </AnimatePresence>
              </div>
            ) : (
              <Link
                key={item.to}
                to={item.to}
                data-testid={`nav-${item.label.toLowerCase().replace(/\s+/g, "-")}`}
                className={`px-3 py-2 text-sm font-medium transition-colors duration-200 ${
                  pagePath === item.to
                    ? "text-green"
                    : onDark
                    ? "text-white/85 hover:text-white"
                    : "text-navy hover:text-green"
                }`}
              >
                {item.label}
              </Link>
            )
          )}
        </div>

        <div className="hidden lg:flex items-center gap-4">
          <LanguageSwitcher onDark={onDark} />
          <Link to="/il-metodo#prenota" data-testid="nav-cta-desktop" className="btn-green">
            <CalendarCheck size={16} />
            {COPY.nav.ctaBooking}
          </Link>
        </div>

        <button
          data-testid="mobile-menu-toggle"
          className={`lg:hidden p-2 transition-colors duration-200 ${onDark && !open ? "text-white" : "text-navy"}`}
          onClick={() => setOpen((v) => !v)}
          aria-label="Menu"
        >
          {open ? <X size={26} /> : <Menu size={26} />}
        </button>
      </nav>

      <AnimatePresence>
        {open && (
          <motion.div
            initial={{ opacity: 0, height: 0 }}
            animate={{ opacity: 1, height: "auto" }}
            exit={{ opacity: 0, height: 0 }}
            transition={{ duration: 0.3 }}
            className="lg:hidden overflow-hidden bg-white border-b border-[#E7EDF2]"
            data-testid="mobile-menu"
          >
            <div className="container-x py-6 flex flex-col gap-1">
              {NAV.map((item) =>
                item.children ? (
                  <div key={item.label}>
                    <button
                      onClick={() => setMobileProd((v) => !v)}
                      data-testid="nav-mobile-prodotti-toggle"
                      aria-expanded={mobileProd}
                      className="w-full flex items-center justify-between py-3 text-base font-medium text-navy"
                    >
                      {item.label}
                      <ChevronDown size={18} className={`transition-transform ${mobileProd ? "rotate-180" : ""}`} />
                    </button>
                    <AnimatePresence>
                      {mobileProd && (
                        <motion.div
                          initial={{ height: 0, opacity: 0 }}
                          animate={{ height: "auto", opacity: 1 }}
                          exit={{ height: 0, opacity: 0 }}
                          className="overflow-hidden pl-4"
                        >
                          <Link to={item.to} data-testid="nav-mobile-dd-tutti-i-prodotti" className="block py-2.5 text-sm font-semibold text-navy hover:text-green">
                            Tutti i prodotti
                          </Link>
                          {item.children.map((c) => (
                            <Link key={c.to} to={c.to} data-testid={`nav-mobile-dd-${c.label.toLowerCase().replace(/\s+/g, "-")}`} className="block py-2.5 text-sm text-navy/70 hover:text-green">
                              {c.label}
                            </Link>
                          ))}
                        </motion.div>
                      )}
                    </AnimatePresence>
                  </div>
                ) : (
                  <Link key={item.to} to={item.to} className="py-3 text-base font-medium text-navy hover:text-green">
                    {item.label}
                  </Link>
                )
              )}
              <LanguageSwitcher mobile />
              <Link to="/il-metodo#prenota" data-testid="nav-cta-mobile" className="btn-green mt-4 w-full">
                <CalendarCheck size={16} />
                {COPY.nav.ctaBooking}
              </Link>
            </div>
          </motion.div>
        )}
      </AnimatePresence>
    </header>
  );
};

export default Navbar;
